import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { api, inBusiness, isApiError } from '@/api/client'
import { detectBrowserTimezone } from '@/lib/timezone'
import { PERSISTED_STORE_KEYS, clearPersistedStores } from '@/stores/persistedKeys'
import { useAuthStore } from '@/stores/useAuthStore'

export interface Business {
  id: string
  name: string
  industry: string | null
  website_url: string | null
  onboarding_completed_at?: string | null
  /**
   * Display-only billing essentials from GET /businesses (#2491) — snake_case
   * straight off the API. Optional: single-business endpoints omit them.
   */
  subscription_tier?: string | null
  credit_balance?: number | null
  /** The caller's role in this business; set by GET /businesses. */
  role?: string | null
  /** Maxflow's own house business, which can't be deleted; set by GET /businesses. */
  is_house?: boolean
}

/**
 * sessionStorage key for the post-switch confirmation (#2491). Written just
 * before the reload `switchBusiness` ends in; read-and-cleared on boot.
 * sessionStorage on purpose: `clearPersistedStores` wipes persisted Zustand
 * keys in localStorage, so this survives the wipe AND the reload, and dies
 * with the tab.
 */
const SWITCHED_BUSINESS_KEY = 'mf.switched-business'

/**
 * Read-and-clear the name of the business a just-finished switch landed in.
 * Returns null when the boot is not the tail end of a business switch.
 */
export function consumeSwitchedBusinessName(): string | null {
  const name = sessionStorage.getItem(SWITCHED_BUSINESS_KEY)
  if (name !== null) sessionStorage.removeItem(SWITCHED_BUSINESS_KEY)
  return name
}

interface MyBusinessState {
  businesses: Business[]
  isLoading: boolean
  error: string | null
  /**
   * Set when `POST /businesses` succeeded but the follow-up
   * `switchBusiness` failed — the business already exists (and is in
   * `businesses`), so the UI must not offer a plain "create again" retry,
   * which would create a duplicate with the same name (#1609, #1634).
   */
  pendingSwitchBusiness: Business | null
}

interface BusinessUpdatePayload {
  name?: string
  website_url?: string | null
}

interface MyBusinessActions {
  fetch: () => Promise<void>
  /** Cancel the in-flight fetch() request, if any. Call from an unmount cleanup. */
  cancelFetch: () => void
  createBusiness: (name: string) => Promise<void>
  /** Make `id` the active business, then reload, or open `landOn` when given. */
  switchBusiness: (id: string, landOn?: string) => Promise<void>
  updateBusiness: (id: string, data: BusinessUpdatePayload) => Promise<void>
  deleteBusiness: (id: string) => Promise<void>
  /** Clear a stale error/pendingSwitchBusiness, e.g. when reopening the dialog. */
  clearBusinessError: () => void
}

type MyBusinessStore = MyBusinessState & MyBusinessActions

const initialState: MyBusinessState = {
  businesses: [],
  isLoading: false,
  error: null,
  pendingSwitchBusiness: null,
}

// Module-level (not store state) — a fresh fetch() call, or an unmounting
// component, aborts whatever request is still in flight. Without this, rapid
// navigation could let a torn-down/superseded request's failure land after a
// newer one already succeeded, and — since the catch block never touched
// `businesses` — leave the selector permanently stuck on "No business" (#2370).
let inFlightController: AbortController | null = null

export const useMyBusinessStore = create<MyBusinessStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      fetch: async () => {
        inFlightController?.abort()
        const controller = new AbortController()
        inFlightController = controller
        set({ isLoading: true, error: null })
        try {
          const response = await api.get<Business[]>('/businesses', { signal: controller.signal })
          set({ businesses: response ?? [], isLoading: false })
        } catch (error) {
          if (error instanceof DOMException && error.name === 'AbortError') {
            // Self-initiated cancellation — never surface as an error, and
            // never clobber the state a newer in-flight fetch may have set.
            return
          }
          const message = isApiError(error) ? error.message : 'Failed to fetch businesses'
          set({ error: message, isLoading: false })
        }
      },

      cancelFetch: () => {
        inFlightController?.abort()
      },

      createBusiness: async (name: string) => {
        set({ isLoading: true, error: null, pendingSwitchBusiness: null })
        let created: Business | undefined
        try {
          created = await api.post<Business>('/businesses', {
            name,
            timezone: detectBrowserTimezone(),
          })
          await get().fetch()
        } catch (error) {
          const message = isApiError(error) ? error.message : 'Failed to create business'
          set({ error: message, isLoading: false })
          throw error
        }

        // Land the user in the business they just created. `POST /businesses`
        // only claims `default_business_id` when the user has none, and every
        // authenticated user already has one (resolving the business context
        // auto-provisions it), so nothing else repoints the active business —
        // the create silently succeeded while the user stayed in the old one
        // (#1609). switchBusiness sets `error` and rethrows on failure, which
        // keeps the dialog open instead of closing on a half-done create.
        if (!created?.id) {
          set({ isLoading: false })
          return
        }
        try {
          await get().switchBusiness(created.id)
        } catch (error) {
          // The business is already created (and in the refreshed list) —
          // only the switch failed. Say so explicitly and name the business,
          // rather than leaving the dialog looking like a plain re-create is
          // the next step: pressing "Create Business" again would create a
          // SECOND business with the same name (#1634).
          const detail = isApiError(error) ? error.message : 'Failed to switch business'
          set({
            error: `"${created.name}" was created, but we couldn't switch you into it (${detail}). Pick it from the selector.`,
            pendingSwitchBusiness: created,
            isLoading: false,
          })
          throw error
        }
      },

      updateBusiness: async (id: string, data: BusinessUpdatePayload) => {
        try {
          const updated = await api.patch<Business>(`/businesses/${id}`, data, inBusiness(id))
          if (updated) {
            // Only the edited fields: the single-business response leaves the list-only
            // ones (role, plan, balance, is_house) unset.
            const { name, website_url } = updated
            set((state) => ({
              businesses: state.businesses.map((b) =>
                b.id === id ? { ...b, name, website_url } : b
              ),
            }))
          }
        } catch (error) {
          const message = isApiError(error) ? error.message : 'Failed to update business'
          set({ error: message })
          throw error
        }
      },

      switchBusiness: async (id: string, landOn?: string) => {
        set({ isLoading: true, error: null })
        try {
          await api.patch('/users/me/default-business', {
            default_business_id: id,
          })
          // The active business is read from the cached auth-store user, so
          // repoint it here too. Without this the reload rehydrates from a
          // persisted user still pointing at the previous business until
          // `/auth/me` comes back, and the switch reads as a no-op.
          const { user, setUser } = useAuthStore.getState()
          if (user) {
            setUser({ ...user, default_business_id: id })
          }
          // Wipe the other tenant's persisted state BEFORE the reload, or it
          // rehydrates straight back into the new business — the previous
          // business's conversations, message text and upload queue included
          // (#1923). `auth-storage` is excluded on purpose: the repoint above
          // has to survive, and a business switch must not log the user out.
          clearPersistedStores({ except: [PERSISTED_STORE_KEYS.auth] })
          // Toast state can't survive the reload — leave the confirmation in
          // sessionStorage for AppShell to pick up on boot (#2491).
          const target = get().businesses.find((b) => b.id === id)
          if (target) sessionStorage.setItem(SWITCHED_BUSINESS_KEY, target.name)
          if (landOn) window.location.assign(landOn)
          else window.location.reload()
        } catch (error) {
          const message = isApiError(error) ? error.message : 'Failed to switch business'
          set({ error: message, isLoading: false })
          throw error
        }
      },

      deleteBusiness: async (id: string) => {
        set({ isLoading: true, error: null })
        try {
          await api.delete(`/businesses/${id}`, inBusiness(id))
        } catch (error) {
          const message = isApiError(error) ? error.message : 'Failed to delete business'
          set({ error: message, isLoading: false })
          throw error
        }

        // The backend blocks deleting the caller's last remaining business
        // (409), so if this succeeded another owned business always exists.
        const wasActive = useAuthStore.getState().user?.default_business_id === id
        await get().fetch()

        if (!wasActive) {
          set({ isLoading: false })
          return
        }

        // A set-up business first, so the owner isn't dropped into onboarding.
        const others = get().businesses.filter((b) => b.id !== id)
        const remaining = others.find((b) => b.onboarding_completed_at) ?? others[0]
        if (!remaining) {
          set({ isLoading: false })
          return
        }
        // switchBusiness reloads the page once the pointer moves — the same
        // clean-state handoff a manual switch performs (#1923).
        await get().switchBusiness(remaining.id)
      },

      clearBusinessError: () => set({ error: null, pendingSwitchBusiness: null }),
    }),
    { name: 'MyBusinessStore', enabled: import.meta.env.DEV }
  )
)
